import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useCalendarHolidaySave } from "@/queries/use-calendar-holidays.query.ts";
import { useDepartmentsSearchInline } from "@/queries/use-departments.query.ts";
import { useDivisionsSearchInline } from "@/queries/use-divisitions.query.ts";
import { useCountries, useLocationsSearchInline } from "@/queries/use-locations.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiCalendarHoliday,
  ApiCalendarHolidaySavePayload,
} from "@/types/calendar-holiday.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Input,
  Modal,
  ModalProps,
  Multiselect,
  Select,
  Stack,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface CalendarHolidayEditModalProps extends ModalProps {
  calendarHoliday: ApiCalendarHoliday;
}

export const CalendarHolidayEditModal = ({
  calendarHoliday,
  ...props
}: CalendarHolidayEditModalProps) => {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();

  const calendarHolidaySchema = z.object({
    title: z.string().min(1, t("errors:field_required")),
    country_id: z.number().nullable().optional(),
    location_ids: z.array(z.number()),
    department_ids: z.array(z.number()),
    division_ids: z.array(z.number()),
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(calendarHolidaySchema),
    values: {
      title: calendarHoliday.title,
      country_id: calendarHoliday.country?.id,
      location_ids: calendarHoliday.locations.filter((x) => !x.is_archived).map((x) => x.id),
      department_ids: calendarHoliday.departments.filter((x) => !x.is_archived).map((x) => x.id),
      division_ids: calendarHoliday.divisions.filter((x) => !x.is_archived).map((x) => x.id),
    },
    defaultValues: {
      location_ids: [],
      department_ids: [],
      division_ids: [],
    },
  });

  const { data: countries } = useCountries();
  const locations = useLocationsSearchInline();
  const divisions = useDivisionsSearchInline();
  const departments = useDepartmentsSearchInline();
  const calendarHolidaySave = useCalendarHolidaySave(control);

  const onSubmit = handleSubmit(async (schema) => {
    const savePayload: ApiCalendarHolidaySavePayload = {
      title: schema.title,
      country_id: schema.country_id || null,
      location_ids: schema.location_ids,
      division_ids: schema.division_ids,
      department_ids: schema.department_ids,
    };
    calendarHolidaySave.mutate(
      { id: calendarHoliday.id, payload: savePayload },
      {
        onSuccess: (data) => {
          if (calendarHoliday.id == 0) {
            analyticsService.trackEvent(analyticEvents.calendarHolidays.created, {
              [analyticProperties.id]: data.id,
            });
          } else {
            analyticsService.trackEvent(analyticEvents.calendarHolidays.edited, {
              [analyticProperties.id]: data.id,
            });
          }
          close(id);
        },
      }
    );
  });

  return (
    <>
      <Modal
        {...props}
        title={t("core:calendar_holiday_policy")}
        withCloser
        actions={
          <Stack gap={"sm"}>
            <ServerErrorField errors={errors} />
            <Button
              type={"submit"}
              size={"lg"}
              isLoading={calendarHolidaySave.isPending}
              onClick={onSubmit}
            >
              {t("common:save")}
            </Button>
          </Stack>
        }
      >
        <form onSubmit={onSubmit}>
          <Stack>
            <Input
              label={t("common:title")}
              required={true}
              autoFocus={true}
              type="text"
              {...register("title")}
              error={errors.title?.message}
            />
            <Controller
              render={({ field: { value, onChange } }) => (
                <Select
                  options={countries || []}
                  disabled={calendarHoliday.id != 0 && !!calendarHoliday.country}
                  emptyTitle={t("core:country")}
                  searchable={true}
                  value={value}
                  onChange={onChange}
                  error={errors.country_id?.message}
                />
              )}
              control={control}
              name={"country_id"}
            />
          </Stack>
          <Stack>
            <Title
              header={t("core:calendar_holidays_title_filter_title")}
              caption={t("core:calendar_holidays_title_filter_description")}
              size={"sm"}
              paddingBottom
              paddingTop
            />
            <Controller
              render={({ field: { value, onChange } }) => (
                <Multiselect
                  label={t("common:locations")}
                  searchable={true}
                  searchOptions={locations.options}
                  emptyTitle={t("common:select")}
                  values={value}
                  onChange={onChange}
                  options={[]}
                  error={errors.location_ids?.message}
                />
              )}
              control={control}
              name={"location_ids"}
            />
            <Controller
              render={({ field: { value, onChange } }) => (
                <Multiselect
                  label={t("core:departments")}
                  searchable={true}
                  searchOptions={departments.options}
                  emptyTitle={t("common:select")}
                  values={value}
                  onChange={onChange}
                  options={[]}
                  error={errors.department_ids?.message}
                />
              )}
              control={control}
              name={"department_ids"}
            />
            <Controller
              render={({ field: { value, onChange } }) => (
                <Multiselect
                  label={t("core:divisions")}
                  searchable={true}
                  searchOptions={divisions.options}
                  emptyTitle={t("common:select")}
                  values={value}
                  onChange={onChange}
                  options={[]}
                  error={errors.division_ids?.message}
                />
              )}
              control={control}
              name={"division_ids"}
            />
          </Stack>
        </form>
      </Modal>
    </>
  );
};
